import { SitesBaseIcon } from '@tableau/icons/SitesBaseIcon';
import { Colors } from '@tableau/icons/util/Colors';
import { SiteUrls } from '@tableau/shared-urls';
import { DropdownButtonType, ListItem, SingleSelectDropdown } from '@tableau/widgets';
import React from 'react';
import { defaultMemoize } from 'reselect';

import { Location } from '~commonInfra/utils/Location';
import { TallListItemCheckmarkCentered } from '~src/commonInfra/styles/CommonStyles';
import { ISiteInfoForLinkEnabledSite } from '~src/siteManagement/serverApi/types/ISiteInfoForLinkEnabledSite';
import { ISitesAndTenantsInfo } from '~src/siteManagement/serverApi/types/ISitesAndTenantsInfo';
import { ITenantInfo } from '~src/siteManagement/serverApi/types/ITenantInfo';
import { NavPanelDisplayMode } from '~src/spacesPlatform/components/layout/NavPanel';

import { Messages } from '../../../codegen/Localize';
import { SitePickerStyles } from '../../styles/SitePickerStyles';
import { LinkEnabledSiteFilteringList } from './LinkEnabledSiteFilterList';

export interface LinkEnabledSitePickerListItem extends ListItem {
  uri?: string;
  isTenant?: boolean;
  canAccess?: boolean;
  displayName?: string;
  id?: string;
  isMigrated?: boolean;
  instanceDomain?: string;
}

export interface LinkEnabledSitePickerOwnProps {
  readonly navPanelDisplayMode: NavPanelDisplayMode;
  readonly onSitePickerClick?: () => void;
  readonly onSelection: () => void;
}

export interface LinkEnabledSitePickerStateProps {
  readonly currentSiteName: string;
  readonly currentSiteUrlName: string;
  readonly sitesAndTenantsInfo: ISitesAndTenantsInfo;
}

export interface LinkEnabledSitePickerDispatchProps {
  requestSitesAndTenantsInfo: () => void;
}

export type LinkEnabledSitePickerProps = LinkEnabledSitePickerOwnProps & LinkEnabledSitePickerStateProps & LinkEnabledSitePickerDispatchProps;

export const SITE_FILTER_LIST_ID = 'site-picker-items-';
export const SITE_ITEM_SUFFIX = '-SITE';
export const TENANT_ITEM_SUFFIX = '-TENANT';

function getSiteUrl(site: ISiteInfoForLinkEnabledSite | LinkEnabledSitePickerListItem) {
  if(!site || !site.uri) {
    return undefined;
  }
  const queryString = window.location.search;
  const siteUrls = new SiteUrls({ siteUrlName: site.uri, queryString: queryString });
  return siteUrls.homeUrl();
}


function getTenantUrl(tenant: ITenantInfo | LinkEnabledSitePickerListItem) {
  if (!tenant || !tenant.instanceDomain) {
    return undefined;
  }
  return tenant.instanceDomain;
}

function getParentTenantId(index: number) {
    return SITE_FILTER_LIST_ID + index;
}

function createSitePickerListItems(sitesAndTenantsInfo: ISitesAndTenantsInfo, currentSiteName: string): LinkEnabledSitePickerListItem[] {
    let managedTenantIdToTenantMap = new Map<string, ITenantInfo>();
    let tenantToMigratedSites = new Map<string, ISiteInfoForLinkEnabledSite[]>();
    let unmigratedSiteList: ISiteInfoForLinkEnabledSite[] = [];
    let tenantsWithAdminAcessList: LinkEnabledSitePickerListItem[] = [];
    let tenantsWithoutAdminAccessList: LinkEnabledSitePickerListItem[] = [];
    let sitePickerListItems: LinkEnabledSitePickerListItem[] = [];

    // Set up map with tenants can manage (are Link Admin on)
    sitesAndTenantsInfo.managedTenants.forEach((tenant: ITenantInfo) => {
        managedTenantIdToTenantMap.set(tenant.id, tenant);
        tenantToMigratedSites.set(tenant.id, []);
    });


    // Separate migrated sites and non-migrated sites, mapping migrated sites to tenant
    sitesAndTenantsInfo.sitesInfo.forEach((siteInfo: ISiteInfoForLinkEnabledSite) => {
        if (siteInfo.isMigrated && siteInfo.parentTenant && Object.keys(siteInfo.parentTenant).length > 0) {
            let sitesForTenant: ISiteInfoForLinkEnabledSite[] =
                tenantToMigratedSites.get(siteInfo.parentTenant.id) !== undefined ?
                tenantToMigratedSites.get(siteInfo.parentTenant.id) : [];
            sitesForTenant.push(siteInfo);
            tenantToMigratedSites.set(siteInfo.parentTenant.id, sitesForTenant);
        } else {
            unmigratedSiteList.push(siteInfo);
        }
    });

    // Create dropdown tenant list items for sites migrated to tenant
    for(let tenantId of Array.from(tenantToMigratedSites.keys())) {
        let sites = tenantToMigratedSites.get(tenantId);
        let tenant: ITenantInfo = managedTenantIdToTenantMap.has(tenantId) ?
            managedTenantIdToTenantMap.get(tenantId) : sites[0].parentTenant;

        if(managedTenantIdToTenantMap.has(tenant.id)) {
            tenantsWithAdminAcessList.push(createTenantListItem(tenant, true, true));
        } else {
            tenantsWithoutAdminAccessList.push(createTenantListItem(tenant, false, true));
        }
    }

    // Sort both tenant lists alphabetically
    tenantsWithAdminAcessList.sort(compareSitePickerListItem);
    tenantsWithoutAdminAccessList.sort(compareSitePickerListItem);

    //Add tenant items with admin access and child site items (sorted) to dropdown first
    for(let tenant of tenantsWithAdminAcessList) {
        let siteItems: LinkEnabledSitePickerListItem[] = [];
        sitePickerListItems.push(tenant);

        let sites = tenantToMigratedSites.get(tenant.id);
        let parentTenantIndex = sitePickerListItems.length - 1;
        sites.forEach((site: ISiteInfoForLinkEnabledSite) => {
            siteItems.push(createSiteListItem(site, currentSiteName, getParentTenantId(parentTenantIndex)));
        });
        siteItems.sort(compareSitePickerListItem);
        sitePickerListItems = sitePickerListItems.concat(siteItems);
        sitePickerListItems.push({separator: true});
    }

    //Add tenant items without admin access and child site items (sorted) to dropdown second
    for(let tenant of tenantsWithoutAdminAccessList) {
        let siteItems: LinkEnabledSitePickerListItem[] = [];
        sitePickerListItems.push(tenant);
        let parentTenantIndex = sitePickerListItems.length - 1;

        if(tenant.isMigrated) {
            let sites = tenantToMigratedSites.get(tenant.id);
            sites.forEach((site: ISiteInfoForLinkEnabledSite) => {
                siteItems.push(createSiteListItem(site, currentSiteName, getParentTenantId(parentTenantIndex)));
            });
            siteItems.sort(compareSitePickerListItem);
            sitePickerListItems = sitePickerListItems.concat(siteItems);
            sitePickerListItems.push({separator: true});
        }
    }

    // then add unmigrated sites
    const unmigratedSiteItems = unmigratedSiteList.map(
      (site: ISiteInfoForLinkEnabledSite) => createSiteListItem(site, currentSiteName));
    unmigratedSiteItems.sort(compareSitePickerListItem);
    sitePickerListItems = sitePickerListItems.concat(unmigratedSiteItems);

    // remove trailing separator
    if(sitePickerListItems[sitePickerListItems.length - 1]?.separator) {
      sitePickerListItems.pop();
    }

    return sitePickerListItems;
}

function compareSitePickerListItem(a: LinkEnabledSitePickerListItem, b: LinkEnabledSitePickerListItem): number {
    if(a.displayName < b.displayName) {
        return -1;
    }
    if(a.displayName > b.displayName) {
        return 1;
    }
    return 0;
}

function createTenantListItem(tenantInfo: ITenantInfo, canManageTenant: boolean, isMigrated: boolean): LinkEnabledSitePickerListItem {
    let displayName = Messages.sitePicker_cloudManager({TENANT_NAME: tenantInfo.name});
    let value = tenantInfo.name + Messages.sitePicker_tenantItemSuffix();
    return {
        uri: tenantInfo.uri,
        id: tenantInfo.id,
        value: value,
        testId: `tenant-${tenantInfo.name}`,
        content: createTenantListItemContent(tenantInfo, displayName),
        isTenant: true,
        disabled: !canManageTenant,
        canAccess: canManageTenant,
        displayName: displayName,
        isMigrated: isMigrated,
        instanceDomain: tenantInfo.instanceDomain
    };
}

function createTenantListItemContent(tenantInfo: ITenantInfo, displayName: string): JSX.Element {
    /* eslint-disable jsx-a11y/alt-text */
  const tenantUrl = getTenantUrl(tenantInfo);
  const testId = `tenant-list-item-${tenantInfo.name}`;
  let title = displayName + Messages.sitePicker_tenantItemSuffix();
  const sharedProps = {
    className: SitePickerStyles.ItemContainer,
    title: title,
    tabIndex: -1
  };
  const siteNameComponent = (
    <div className={SitePickerStyles.TenantCloudManagerItem}>
        {displayName}
    </div>
  );

  return <>
    { tenantUrl ?
      <a {...sharedProps} data-tb-test-id={testId} href={tenantUrl}> {siteNameComponent} </a> :
      <div {...sharedProps} data-tb-test-id={testId}> {siteNameComponent} </div>
    }
  </>;
  }

/**
 * @param siteInfo site to construct site item for
 * @param parentTenantId required if there is a parent tenant, for accessibility
 * @returns a `LinkEnabledSitePickerListItem` based on provided `siteInfo`
 */
function createSiteListItem(siteInfo: ISiteInfoForLinkEnabledSite, currentSiteName: string, parentTenantId?: string): LinkEnabledSitePickerListItem {
    let value = siteInfo.uri + Messages.sitePicker_siteItemSuffix();
    return {
        uri: siteInfo.uri,
        value: value,
        testId: `site-${siteInfo.name}`,
        content: createSiteListItemContent(siteInfo, currentSiteName),
        isTenant: false,
        displayName: siteInfo.name,
        canAccess: true,

        // Attaching id of tenant parent to make sure item meets accessbility reqs for ListBox
        'aria-describedby': parentTenantId
      };
}

function createSiteListItemContent(siteInfo: ISiteInfoForLinkEnabledSite, currentSiteName: string): JSX.Element {
    const isCurrentSite = currentSiteName + SITE_ITEM_SUFFIX === siteInfo.name + SITE_ITEM_SUFFIX;
    let title = siteInfo.name + Messages.sitePicker_siteItemSuffix();
    /* eslint-disable jsx-a11y/alt-text */
    return (
      <a
        className={SitePickerStyles.ItemContainer}
        data-tb-test-id={`site-list-item-${siteInfo.uri}`}
        href={getSiteUrl(siteInfo)}
        title={title}
        tabIndex={-1}
      >
        <div className={SitePickerStyles.LinkEnabledSiteItem} style={{ paddingLeft: siteInfo.isMigrated || isCurrentSite ? 24 : undefined}}>
          {siteInfo.name}
        </div>
      </a>
    );
  }

export class LinkEnabledSitePicker extends React.Component<LinkEnabledSitePickerProps> {

  private memoizedSitesListItems = defaultMemoize(createSitePickerListItems);

  public render(): JSX.Element {
    // test id for e2e test backward compat
    let value = this.props.currentSiteUrlName + Messages.sitePicker_siteItemSuffix();
    return (
      <SingleSelectDropdown
        children={this.getButtonContent()}
        type={DropdownButtonType.Text}
        list={this.getSiteList()}
        value={value}
        style={this.props.navPanelDisplayMode === 'icon' ? SitePickerStyles.SitePickerMinimized : SitePickerStyles.SitePickerExpanded}
        testId="site-switcher-button"
        onOpen={this.handleOpen}
        onClose={this.handleClose}
        onChange={this.handleChange}
        label={''}
        aria-controls={'site-picker-popup'}
        aria-label={Messages.siteSwitcher_choose()}
      />
    );
  }

  private getSiteList = () => {
    const listItems = this.memoizedSitesListItems(this.props.sitesAndTenantsInfo, this.props.currentSiteName);

    /* eslint-disable jsx-a11y/no-autofocus */
    return (
      <LinkEnabledSiteFilteringList
        items={listItems}
        requestSitesAndTenantsInfo={this.props.requestSitesAndTenantsInfo}
        autoFocus={true}
        className={TallListItemCheckmarkCentered}
        searchPlaceholder={Messages.searchSites()}
        style={SitePickerStyles.List}

        listId={SITE_FILTER_LIST_ID}

        // test ids for e2e test compatibility
        inputTestId="site-switcher-textbox-input"
        listTestId="site-picker-items"
        id="site-picker-popup"
      />
    );
  };

  private closePanel: () => void;

  private handleOpen = (done: () => void): void => {
    // save close panel for later
    this.closePanel = done;

    if (this.props.onSitePickerClick) {
      this.props.onSitePickerClick();
    }
  };

  private handleClose = (): void => {
    if (this.closePanel) {
      this.closePanel();
      this.closePanel = null;
    }

    this.props.onSelection();
  };

  private handleChange = (value: string) => {
    if (this.closePanel) {
      const items = this.memoizedSitesListItems(this.props.sitesAndTenantsInfo, this.props.currentSiteName);
      const item = items.find((item) => item.value === value);
      if (item) {
        const urlToNavigateTo = item.isTenant ? getTenantUrl(item) : getSiteUrl(item);
        if (urlToNavigateTo) {
          Location.instance.setUrl(urlToNavigateTo);
        }
      }
      this.handleClose();
    }
  };

  private getButtonContent = () => {
    if (this.props.navPanelDisplayMode === 'icon') {
      return(
        <span style={{width:18, height:18}}>
          <SitesBaseIcon size={18} color={Colors.Default}/>
        </span>
      );
    } else {
      return <span className={SitePickerStyles.SitePickerButton}>{this.props.currentSiteName}</span>;
    }
  };
}
